// @flow
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { uniqBy, partial } from 'lodash';

import PaymentMethodImageStrip from '@zapier/payment/src/components/PaymentMethodImageStrip';
import { Button, ButtonGroup, Colors, Text } from '@zapier/design-system';
import { type Currency } from '@zapier/payment/src/hooks/useCurrencies';
import { type CurrencyRulesetItem } from '@zapier/payment/src/hooks/useCurrencyRuleset';
import {
  type PaymentSourceMethod,
  paymentSourceMethods,
} from '@zapier/payment/src/types';

import PaymentMethodSelect from '../PaymentMethodSelect';

type Props = {
  /** Currently selected method */
  currentMethod?: PaymentSourceMethod,
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceMethod, hasSwitched: boolean) => void,
  /** The currently selected currency */
  selectedCurrency: Currency,
  /** Array of supported payment methods */
  supportedMethods: Array<CurrencyRulesetItem>,
};

const flexCss = css`
  display: flex;
  justify-content: center;
`;

const sectionCss = css`
  border: 1px dashed ${Colors.grey200};
  border-radius: 10px;
  padding: 15px;
  text-align: center;

  @media screen and (min-width: 600px) : {
    padding: 40px;
  }
`;

const selectCss = css`
  margin-top: 20px;
`;

const joinWithLast = (
  list: Array<string>,
  joiner: string,
  last: string
): ?string => {
  if (list.length < 2) {
    return null;
  }
  const fullList = list.join(joiner);
  const lastJoin = fullList.lastIndexOf(joiner);
  return (
    fullList.substring(0, lastJoin) +
    last +
    fullList.substring(lastJoin + joiner.length)
  );
};

const methodResolver = {
  [paymentSourceMethods.card]: {
    addText: 'Add Card',
  },
  [paymentSourceMethods.paypal]: {
    addText: 'Connect PayPal',
  },
  [paymentSourceMethods.bank]: {
    addText: 'Add Bank Account',
  },
};

const NoPaymentInformation = ({
  currentMethod,
  onSelectSource,
  selectedCurrency,
  supportedMethods,
}: Props) => {
  const acceptText = joinWithLast(
    supportedMethods.map(item => item.submethodDisplay),
    ', ',
    ' or '
  );

  const uniqueMethods = uniqBy(supportedMethods, m => m.method);

  return (
    <section css={sectionCss}>
      <h3>
        <Text type="subtitle800">No Payment Info</Text>
      </h3>
      {acceptText && (
        <p>
          <Text type="bodyText800">
            We accept {acceptText} for payments in {selectedCurrency.name}{' '}
            {selectedCurrency.symbol}.
          </Text>
        </p>
      )}
      <div css={flexCss}>
        <PaymentMethodImageStrip methods={supportedMethods} />
      </div>
      <div css={selectCss}>
        {uniqueMethods.length < 3 ? (
          <ButtonGroup columnSize="minmax(100px, 200px)" justify="center">
            {uniqueMethods
              .map(m => m.method)
              .map(m => {
                return (
                  <Button
                    ariaLabel={methodResolver[m].addText}
                    color="greyAlt"
                    key={m}
                    onClick={partial(onSelectSource, m, false)}
                  >
                    {methodResolver[m].addText}
                  </Button>
                );
              })}
          </ButtonGroup>
        ) : (
          uniqueMethods.length && (
            <div css={flexCss}>
              <PaymentMethodSelect
                currentMethod={currentMethod}
                onChange={onSelectSource}
                supportedMethods={uniqueMethods}
              />
            </div>
          )
        )}
      </div>
    </section>
  );
};

export default NoPaymentInformation;
